import { useEffect } from "react";

import get from "lodash/get";
import { useFormContext } from "react-hook-form";

import { useFormkitContext } from "src/formkit/components/formkit-context";

import { OVERRIDE_CONFIG_FORM_KEY } from "./constants";

/**
 * This hook is used to ensure that the dependent field names are unlocked/locked
 * when the `fieldName` field is unlocked/locked
 *
 * It is used in the following cases:
 * - TEMPLATES: ensure that the dependent field names are unlocked/locked
 * - SYNCS WITH OVERRIDES: Set the missing relationship in the formkit context so
 *     that other components may react to it and reset values when needed
 */
export const useMissingFormkitRelationships = (
  fieldName: string,
  dependentFieldNames: string[],
) => {
  const { showLockUI, relationshipHierarchy, onAddMissingRelationships } =
    useFormkitContext();
  const { setValue, watch } = useFormContext();

  const overrideConfig = watch(OVERRIDE_CONFIG_FORM_KEY);

  // TEMPLATES: ensure that the dependent field names are unlocked/locked
  // when the `fieldName` field is unlocked/locked
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (showLockUI) {
      for (const dependentFieldName of dependentFieldNames) {
        const overridableValue = get(overrideConfig, [
          fieldName,
          "overridable",
        ]);

        if (overridableValue !== undefined) {
          setValue(`${OVERRIDE_CONFIG_FORM_KEY}.${dependentFieldName}`, {
            overridable: overridableValue,
          });
        }
      }
    }
  }, [showLockUI, overrideConfig, dependentFieldNames]);

  // Set the missing relationship in the formkit context so that other components may react to it
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (
      relationshipHierarchy &&
      relationshipHierarchy[fieldName] === undefined
    ) {
      onAddMissingRelationships(fieldName, dependentFieldNames);
    }
  }, [fieldName, dependentFieldNames, relationshipHierarchy]);
};
